import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/* ===========================================================================
 * tailwind-merge has to be taught this project's custom scales.
 *
 * Out of the box it only knows Tailwind's stock values. Every token this
 * design system adds in `apps/web/tailwind.config.ts` (`text-copy`,
 * `p-card`, `rounded-control`, `shadow-elev-md`, …) is therefore an unknown
 * string to it — and for one group that is not merely a missed merge, it is
 * silent data loss:
 *
 *   twMerge('text-label-lg', 'text-primary-foreground')  ->  'text-primary-foreground'
 *
 * `text-<anything>` matches tailwind-merge's *colour* validator (a colour can
 * be any word), so a custom type token looks like a colour, collides with the
 * real colour, and loses. That is not hypothetical: it is why `<Button>` has
 * been shipping without its `text-label-lg` — the base class and the variant's
 * `text-primary-foreground` went through one `cn()` and the size was dropped
 * before it ever reached the DOM. Buttons have been rendering at whatever font
 * size they inherited.
 *
 * Registering the scales below fixes that class of bug for good, and as a
 * bonus makes the other groups actually mergeable, so a call site can still
 * override a primitive (`<CardContent className="p-4">` now beats `p-card`,
 * where before both were emitted and CSS source order silently decided).
 *
 * Keep these lists in step with `theme.extend` in the Tailwind config.
 * ======================================================================== */

/** Custom `fontSize` keys — both scales. See `theme.extend.fontSize`. */
const FONT_SIZES = [
  // Material 3 type scale.
  'display-lg', 'display-md', 'display-sm',
  'headline-lg', 'headline-md', 'headline-sm',
  'title-lg', 'title-md', 'title-sm',
  'body-lg', 'body-md', 'body-sm',
  'label-lg', 'label-md', 'label-sm',
  // Admin-console density scale.
  'page-title', 'metric', 'metric-md', 'metric-sm',
  'h1', 'h2', 'h3',
  'copy-lg', 'copy', 'copy-sm',
  // `figure` — the body-sized numeral step (see ../../tailwind-type.ts). It
  // is registered here for the reason this file's header gives: `CalendarMonth`
  // passes `text-figure` through `cn()` alongside `text-foreground`, and an
  // unregistered size is read as a colour and silently dropped.
  'figure',
  'label', 'eyebrow', 'badge',
  // `dense` was missing, and its absence is the exact bug this file's header
  // describes — `text-<anything>` also matches tailwind-merge's COLOUR
  // validator, so an unlisted size is read as a colour and the real colour
  // wins. Verified with the installed tailwind-merge:
  //   cn('text-dense', 'text-muted-foreground') -> 'text-muted-foreground'
  // i.e. the size was silently dropped. Every current call site passes a
  // literal className, so nothing renders wrong today — this is the landmine,
  // not the explosion.
  'dense',
  // `eyebrow` was the same landmine, already gone off: `Sidebar.tsx`'s
  // `SectionHeading` calls `cn(EYEBROW, 'py-2 text-nav-on-surface/60', …)`,
  // where `EYEBROW` ends in `text-eyebrow`. Because `eyebrow` wasn't
  // registered here, twMerge read it as a colour utility, saw the later
  // `text-nav-on-surface/60` as the "real" colour for the same group, and
  // dropped `text-eyebrow` entirely — so the nav rail's "My workspace"
  // heading rendered at the ambient 16px body size in sentence case instead
  // of the intended 12px uppercase eyebrow, and read as just another nav row
  // instead of the section label over it. `page-title` and `metric-md` were
  // never exercised through a colliding `cn()` call, so nothing broke visibly
  // yet — but they are the same unregistered-key shape and belong in this
  // list before they do.
];

/** Custom `spacing` keys — serve padding, margin, gap and size utilities. */
const SPACING = [
  'btn-y-sm', 'btn-x-sm', 'btn-y-md', 'btn-x-md', 'btn-y-lg', 'btn-x-lg',
  'card', 'tile', 'modal', 'cell-y', 'cell-x',
  'section', 'component', 'element',
  'sidebar', 'sidebar-collapsed', 'header',
];

/**
 * Custom `width` keys that are NOT spacing keys — see `theme.extend.width` in
 * ../../tailwind-preset.ts. Kept apart from `SPACING` on purpose: `w-search` is
 * a width and only a width, and folding it into the spacing list would also
 * teach twMerge to merge `p-search` and `gap-search`, which do not exist.
 *
 * It has to be registered SOMEWHERE, though, or this file's own header bug
 * applies to it: an unknown `w-*` is in no class group, so `cn('w-search',
 * 'w-full')` emits BOTH and CSS source order — not the call site — decides
 * which one wins. That is exactly the silent override `controlBarSearchClass`
 * exists to prevent.
 */
const WIDTHS = ['search', 'menu'];

/**
 * Custom `minHeight` / `minWidth` ROLE names — see `theme.extend.minHeight` and
 * `theme.extend.minWidth` in ../../tailwind-preset.ts.
 *
 * These are deliberately NOT in `SPACING`, and the preset says why: `spacing` is
 * shared by gap, padding, margin, width and height, so a name in it has to mean
 * one thing in all five, and a control HEIGHT does not. But leaving them
 * unregistered here is this file's own header bug, one group over: an unknown
 * `min-h-*` belongs to no class group, so
 *
 *   cn('min-h-control', 'min-h-10')  ->  'min-h-control min-h-10'
 *
 * emits BOTH, and CSS source order — not the call site — decides which wins.
 * That is the same silent override `WIDTHS` exists to prevent, and it had
 * already gone off: `IconTool` hard-codes `min-h-control` (38px, right in a
 * toolbar), so the page-header cluster passing `min-h-10` to raise it to the
 * cluster's 40px could not reliably beat it. Every `min-h-*`/`min-w-*` override
 * in the app was a coin toss until this list existed.
 */
const CONTROL_SIZES = ['chip', 'control', 'touch', 'pill', 'ask'];

/** Custom `borderRadius` role names. */
const RADII = ['2xs', 'xs', 'control', 'card', 'panel', 'modal'];

/** Custom `boxShadow` names — M3 levels plus the console elevation ramp. */
const SHADOWS = [
  'elev-xs', 'elev-sm', 'elev-md', 'elev-lg', 'elev-xl', 'elev-2xl',
  'brand-hover',
];

/** Custom `transitionDuration` names. */
const DURATIONS = ['fast', 'normal', 'slow'];

/** Build `{ [prefix]: SPACING }` entries for a list of spacing-based groups. */
const spacingGroups = (
  prefixes: readonly string[]
): Record<string, Array<Record<string, string[]>>> =>
  Object.fromEntries(prefixes.map((p) => [p, [{ [p]: SPACING }]]));

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: FONT_SIZES }],
      ...spacingGroups([
        'p', 'px', 'py', 'ps', 'pe', 'pt', 'pr', 'pb', 'pl',
        'm', 'mx', 'my', 'ms', 'me', 'mt', 'mr', 'mb', 'ml',
        'gap', 'gap-x', 'gap-y',
        'space-x', 'space-y',
        'w', 'h', 'size', 'min-w', 'min-h', 'max-w', 'max-h',
      ]),
      // AFTER the spread, deliberately: this replaces the `w` group that
      // `spacingGroups` just built so the width-only keys join the spacing ones
      // instead of shadowing them.
      w: [{ w: [...SPACING, ...WIDTHS] }],
      // Same reasoning as `w` above, and for the same reason placed AFTER the
      // spread: these replace the groups `spacingGroups` just built so the
      // control-role names join the spacing ones instead of being shadowed.
      'min-h': [{ 'min-h': [...SPACING, ...CONTROL_SIZES] }],
      'min-w': [{ 'min-w': [...SPACING, ...CONTROL_SIZES, ...WIDTHS] }],
      rounded: [{ rounded: RADII }],
      'rounded-s': [{ 'rounded-s': RADII }],
      'rounded-e': [{ 'rounded-e': RADII }],
      'rounded-t': [{ 'rounded-t': RADII }],
      'rounded-b': [{ 'rounded-b': RADII }],
      'rounded-ss': [{ 'rounded-ss': RADII }],
      'rounded-se': [{ 'rounded-se': RADII }],
      'rounded-ee': [{ 'rounded-ee': RADII }],
      'rounded-es': [{ 'rounded-es': RADII }],
      shadow: [{ shadow: SHADOWS }],
      duration: [{ duration: DURATIONS }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
